import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import CustomAlert from './CustomAlert';
import './ForgotPassword.css';
import logo from '../assets/xtrafyls.svg';
import { signin_profile, password_reset_icon, emailIcon } from '../assets';
import { sendForgotPasswordOtp, verifyForgotPasswordOtp, clearError, setVerificationEmail } from '../features/auth/authSlice';

// Validation Schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required')
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      'Please enter a valid email address'
    ),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const { isLoading, error, otpSent } = useSelector((state) => state.auth);
  
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Custom Alert State
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ type: 'success', title: '', message: '' });

  // Custom Alert Helper
  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  // Initial form values
  const initialValues = {
    email: '',
  };

  // Show OTP popup when OTP is sent
  useEffect(() => {
    if (otpSent) {
      setShowOtpPopup(true);
      setTimer(60);
      setCanResend(false);
    }
  }, [otpSent]);

  // Show error if exists
  useEffect(() => {
    if (error) {
      showCustomAlert('error', 'Password Reset Error', error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Timer effect
  useEffect(() => {
    if (showOtpPopup && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(countdown);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [showOtpPopup, timer]);

  // Disable body scroll when popup is open
  useEffect(() => {
    if (showOtpPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showOtpPopup]);

  // Close popup on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showOtpPopup) {
        handleClosePopup();
      }
    };
    
    if (showOtpPopup) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showOtpPopup]);

  // Form submission handler - Send OTP
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Save submitted email
      setSubmittedEmail(values.email);

      // Dispatch Redux action
      const resultAction = await dispatch(sendForgotPasswordOtp(values.email));

      if (sendForgotPasswordOtp.fulfilled.match(resultAction)) {
        // Save email in Redux state
        dispatch(setVerificationEmail(values.email));
      }
    } catch (error) {
      // Error sending OTP
    } finally {
      setSubmitting(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
      
      // Focus last filled input
      const lastIndex = Math.min(newOtp.length - 1, 5);
      const lastInput = document.getElementById(`otp-input-${lastIndex}`);
      if (lastInput) lastInput.focus();
    }
  };

  // Handle verify OTP
  const handleVerifyOtp = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      showCustomAlert('error', 'Incomplete OTP', 'Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);

    try {
      const resultAction = await dispatch(verifyForgotPasswordOtp({
        email: submittedEmail,
        otp: otpValue
      }));

      if (verifyForgotPasswordOtp.fulfilled.match(resultAction)) {
        // Close popup
        setShowOtpPopup(false);

        // Small delay for smooth transition
        setTimeout(() => {
          navigate('/new-password');
        }, 300);
      }
    } catch (error) {
      // Error verifying OTP
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP handler
  const handleResend = async () => {
    if (!canResend || !submittedEmail) return;

    try {
      // Dispatch Redux action
      await dispatch(sendForgotPasswordOtp(submittedEmail));

      // Reset timer and OTP
      setTimer(60);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);

      // Focus first input
      const firstInput = document.getElementById('otp-input-0');
      if (firstInput) firstInput.focus();
    } catch (error) {
      // Error resending OTP
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Close popup handler
  const handleClosePopup = () => {
    setShowOtpPopup(false);
    setOtp(['', '', '', '', '', '']);
    setTimer(60);
    setCanResend(false);
  };

  return (
    <div className="forgot-password-container">
      {/* Left Side - Purple Hero Section */}
      <div className="forgot-password-left">
        <div className="left-content">
          {/* Logo */}
          <img src={logo} alt="Xtrafyl" className="logo" />

          {/* Hero Text */}
          <div className="hero-section">
            <h1 className="hero-title">
              Fuel Your Business<br />
              With <span className="highlight">Xtrafyl.</span>
            </h1>
            <p className="hero-description text-big-body">
              From fuel delivery to charging, car wash, and more — we help you reach
              more customers, every day.
            </p>
          </div>
        </div>

      </div>

      {/* Right Side - Forgot Password Form */}
      <div className="forgot-password-right">
        <div className="forgot-password-right-content">
          {/* Header */}
          <div className="form-header">
            <h2>Forgot Password?</h2>
            <p className="text-small-body">
              To begin the password reset process, please provide the email address where you
              would like to receive the instructions. We'll send you a code to reset your password.
            </p>
          </div>

          <div className="form-container">
            {/* Formik Form */}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {({ errors, touched, isSubmitting, isValid, dirty }) => (
                <Form className="forgot-password-form">
                  {/* Email Input */}
                  <div className="form-group">
                    <label htmlFor="email" className="text-label">Email Id</label>
                    <div className="input-wrapper">
                      <img src={emailIcon} alt="" className="input-icon" />
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Email Id"
                        className={`text-small-body ${
                          errors.email && touched.email ? 'input-error' : ''
                        } ${!errors.email && touched.email ? 'input-success' : ''}`}
                      />
                      {!errors.email && touched.email && (
                        <svg className="input-success-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <ErrorMessage name="email" component="div" className="error-message" />
                  </div>

                  {/* Send Email Button */}
                  <button
                    type="submit"
                    className="send-email-button text-button"
                    disabled={isSubmitting || isLoading || !isValid || !dirty}
                  >
                    {isSubmitting || isLoading ? 'SENDING...' : 'SEND EMAIL'}
                  </button>

                  {/* Sign In Link */}
                  <p className="signin-link text-small-body">
                    Remember your password? <span onClick={() => navigate('/signin')}>Sign In</span>
                  </p>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* OTP Verification Popup - Exact like SignUp */}
      {showOtpPopup && (
        <div className="otp-popup-overlay" onClick={(e) => {
          if (e.target.className === 'otp-popup-overlay') {
            handleClosePopup();
          }
        }}>
          <div className="otp-popup-content">
            {/* Close Button */}
            <button className="otp-popup-close" onClick={handleClosePopup}>
              ✕
            </button>

            {/* Icon */}
            <div className="otp-icon">
              <img src={password_reset_icon} alt="Password Reset" style={{ width: '120px', height: '120px' }} />
            </div>

            {/* Title & Description */}
            <h2>Verify Your Email..</h2>
            <p className="otp-description">
              We've sent a 6-digit verification code to<br />
              <strong>{submittedEmail}</strong>.<br />
              Enter the code below to continue.
            </p>

            {/* OTP Inputs */}
            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="otp-input"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={handleVerifyOtp}
              className="verify-otp-button"
              disabled={isVerifying || otp.join('').length !== 6}
            >
              {isVerifying ? 'VERIFYING...' : 'VERIFY OTP'}
            </button>

            {/* Resend Section */}
            <div className="otp-resend-section">
              <p className="otp-resend-text">Didn't receive an OTP?</p>
              <button
                onClick={handleResend}
                className={`otp-resend-button ${canResend ? 'active' : 'disabled'}`}
                disabled={!canResend}
              >
                {canResend ? 'Resend' : `Resend: ${formatTime(timer)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default ForgotPassword;